import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { ModalError } from '../../components/modal/ModalError';
import { ModalLoading } from '../../components/modal/ModalLoading';
import { ModalSuccess } from '../../components/modal/ModalSuccess';
import { useGetSignedInUser } from '../../hooks/useGetSignedInUser';
import { APP_URLS } from '../../routes/urls';
import { ApplyForProgramsOrScholarshipsResponse, EligibilityFailResult } from '../../store/features/apply/types';
import { BaseQueryError, ValidationErrorData } from '../../store/types';
import { EligibilityItem } from '../../components/eligibility-fail-content/types';
import { applyScreenStyles } from './styles';
import _ from 'lodash';
import { EligibilityFailContent } from '../../components/eligibility-fail-content/EligibilityFailContent';

interface ApplyFeedbackProps {
  isError: boolean;
  error: BaseQueryError | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  data: ApplyForProgramsOrScholarshipsResponse | undefined;
  onCloseBtnClick: () => void;
  onRetry: () => void;
  eligibilityItems: EligibilityItem | EligibilityItem[];
}

interface ApplySuccessFeedbackProps {
  data: ApplyForProgramsOrScholarshipsResponse;
  onCloseBtnClick: () => void;
  eligibilityItems: EligibilityItem | EligibilityItem[];
}

const ApplySuccessFeedback: React.FC<ApplySuccessFeedbackProps> = ({ data, onCloseBtnClick, eligibilityItems }) => {
  const { currentData: userData } = useGetSignedInUser();
  if (!data.isEligible) return null;
  return (
    <>
      <DialogTitle sx={{ bgcolor: 'success.main', color: 'common.white', fontSize: '1.125rem!important', py: 1.5 }}>
        {data.message}
      </DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        <EligibilityFailContent
          onCloseBtnClick={onCloseBtnClick}
          eligibilityItems={eligibilityItems}
          results={data.results as EligibilityFailResult[]}
        />
      </DialogContent>
      <DialogActions>
        <Grid justifyContent="flex-end" container spacing={1.5}>
          {userData?.user && (
            <Grid item>
              <Button
                component={Link}
                to={generatePath(APP_URLS.applicationInfo, { studentSlug: userData.user.slug })}
                variant="default"
              >
                View Applications
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button onClick={onCloseBtnClick} variant="contained">
              Close
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

export const ApplyFeedback: React.FC<ApplyFeedbackProps> = ({
  isLoading,
  isError,
  isSuccess,
  error,
  data,
  onCloseBtnClick,
  onRetry,
  eligibilityItems,
}) => {
  const { currentData: userData } = useGetSignedInUser();
  const navigate = useNavigate();
  const navigateToApplicationPage = (): void => {
    if (userData?.user) {
      navigate({ pathname: generatePath(APP_URLS.applicationInfo, { studentSlug: userData.user.slug }) });
    }
  };

  const getFeedbackUI = (): React.ReactNode => {
    if (isLoading) {
      return (
        /* Loading Screen */
        <ModalLoading loadingTitle="Checking your eligibility and submitting your applications.." />
      );
    }

    if (isError) {
      let message: string | undefined = error?.message;
      if (error?.status === 422) {
        const errors = (error.data as ValidationErrorData).errors;
        message = _.first(_.first(_.values(errors)));
      }
      return (
        /* Server Error Screen */
        <ModalError
          errorTitle={message}
          errorStatus={error?.status}
          cancelBtnLabel="Close"
          handleCancel={onCloseBtnClick}
          handleRetry={error?.status !== 422 ? onRetry : undefined}
        />
      );
    }

    return null;
  };

  if (isSuccess && data && data.isEligible) {
    const canShowEligibilityDetails = userData?.user?.educations ? userData.user.educations.length > 0 : false;
    return canShowEligibilityDetails ? (
      <ApplySuccessFeedback data={data} onCloseBtnClick={onCloseBtnClick} eligibilityItems={eligibilityItems} />
    ) : (
      <DialogContent sx={applyScreenStyles.centeredDialogContent}>
        <Box sx={{ py: 3 }}>
          <ModalSuccess
            secondaryBtnLabel="Close"
            handleSecondaryBtnClick={onCloseBtnClick}
            primaryBtnLabel={userData?.user && 'View Applications'}
            handlePrimaryBtnClick={userData?.user && navigateToApplicationPage}
            successMessage={data?.message}
          />
        </Box>
      </DialogContent>
    );
  }

  const feedbackUI = getFeedbackUI();
  if (feedbackUI)
    return (
      <DialogContent sx={applyScreenStyles.centeredDialogContent}>
        <Box sx={{ py: 3 }}>{feedbackUI}</Box>
      </DialogContent>
    );
  return null;
};
